import { createSignal, onMount, For, Show, createMemo } from "solid-js";
import styles from "./AppEventView.module.css";
import { AppEventOverview } from "../models/AppEventOverview";
import { AppEventType } from "../models/AppEventType";
import { AppEvent } from "../models/AppEvent";
import { PerformanceData } from "../models/PerformanceData";
import { useApi, isOk } from "../api";
import type { AppEventType as ApiAppEventType } from "../api";
import { useToast } from "../components/MessageBox";
import { useTranslation } from "../hooks/useTranslation";
import { formatDateTime } from "../services/dateFormat";

export default function AppEventView() {

    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [overview, setOverview] = createSignal<AppEventOverview[]>([]);
    const [selectedType, setSelectedType] = createSignal<AppEventType | null>(null);
    const [eventsForCurrentEventType, setEventsForCurrentEventType] = createSignal<AppEvent[]>([]);
    const [locations, setLocations] = createSignal<string[]>([]);
    const [locationFilter, setLocationFilter] = createSignal<string | null>(null);
    const [dateFrom, setDateFrom] = createSignal<Date | null>(new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000));
    const [dateTo, setDateTo] = createSignal<Date | null>(new Date());

    const [performanceDataForLocations, setPerformanceDataForLocations] = createSignal<{ [key: string]: PerformanceData }>({});

    onMount(fetchAll);

    async function fetchAll() {
        try {
            const overviewRes = await api.appEventView.get();
            if (!isOk(overviewRes.data)) { toast?.error(t("APPEVENT_LOAD_EVENTS_FAILED")); return; }
            setOverview((overviewRes.data.value.items ?? []) as unknown as AppEventOverview[]);

            const locRes = await api.appEventView.getAllLoggedLocations();
            if (!isOk(locRes.data)) { toast?.error(t("APPEVENT_LOAD_LOCATIONS_FAILED")); return; }
            const locData: string[] = locRes.data.value.locations ?? [];
            setLocations(locData);

            const perfData: { [key: string]: PerformanceData } = {};
            const results = await Promise.all(locData.map(async (loc) => {
                const perfRes = await api.appEventView.getPerformanceData({
                    where: loc,
                    from: dateFrom() ? dateFrom()!.toISOString() : "",
                    to: dateTo() ? dateTo()!.toISOString() : "",
                });
                return { loc, perfJson: isOk(perfRes.data) ? perfRes.data.value : null };
            }));
            for (const { loc, perfJson } of results) {
                if (perfJson) perfData[loc] = perfJson as unknown as PerformanceData;
            }
            setPerformanceDataForLocations(perfData);
        } catch {
            toast?.error(t("APPEVENT_LOAD_FAILED"));
        }
    }

    function setSelectedEventType(type: AppEventType) {
        if (selectedType() === type) {
            setSelectedType(null);
        } else {
            setSelectedType(type);
            void fetchEventsForType(type);
        }
    }

    async function fetchEventsForType(type: AppEventType) {
        try {
            const res = await api.appEventView.getByType(AppEventType[type] as unknown as ApiAppEventType);
            if (!isOk(res.data)) { toast?.error(t("APPEVENT_LOAD_EVENTS_FAILED")); return; }
            setEventsForCurrentEventType((res.data.value.events ?? []) as unknown as AppEvent[]);
        } catch {
            toast?.error(t("APPEVENT_LOAD_EVENTS_FAILED"));
        }
    }

    function updateDateFrom(date: Date | null) {
        setDateFrom(date);
        fetchAll();
    }

    function updateDateTo(date: Date | null) {
        setDateTo(date);
        fetchAll();
    }

    const filteredLocations = createMemo(() =>
        locations().filter(loc => !locationFilter() || loc.includes(locationFilter()!))
    );

    return (
    <div class={styles.container}>
        <h1 class={styles.title}>{t("APPEVENT_TITLE")}</h1>
        <div class="row">
            <div class="col">
                <label>{t("APPEVENT_DATE_FROM")}</label>
                <input type="date" onInput={e => updateDateFrom(e.currentTarget.value ? new Date(e.currentTarget.value) : null)} value={dateFrom() ? dateFrom()!.toISOString().split("T")[0] : ""} />
            </div>
            <div class="col">
                <label>{t("APPEVENT_DATE_TO")}</label>
                <input type="date" onInput={e => updateDateTo(e.currentTarget.value ? new Date(e.currentTarget.value) : null)} value={dateTo() ? dateTo()!.toISOString().split("T")[0] : ""} />
            </div>
        </div>
        <div class={styles.content}>
            <div class={styles.overview}>
                <div onClick={() => setSelectedEventType(AppEventType.Debug)} class={selectedType() === AppEventType.Debug ? styles.selected : undefined}>
                    <h2>{t("APPEVENT_DEBUG")}</h2>
                    <h1>{overview().find(item => item.eventType === AppEventType.Debug)?.count ?? 0}</h1>
                </div>
                <div onClick={() => setSelectedEventType(AppEventType.Info)} class={selectedType() === AppEventType.Info ? styles.selected : undefined}>
                    <h2>{t("APPEVENT_INFO")}</h2>
                    <h1>{overview().find(item => item.eventType === AppEventType.Info)?.count ?? 0}</h1>
                </div>
                <div onClick={() => setSelectedEventType(AppEventType.Warning)} class={(selectedType() === AppEventType.Warning ? styles.selected : undefined) + " " + styles.warning}>
                    <h2>{t("APPEVENT_WARNING")}</h2>
                    <h1>{overview().find(item => item.eventType === AppEventType.Warning)?.count ?? 0}</h1>
                </div>
                <div onClick={() => setSelectedEventType(AppEventType.Error)} class={(selectedType() === AppEventType.Error ? styles.selected : undefined) + " " + styles.error}>
                    <h2>{t("APPEVENT_ERROR")}</h2>
                    <h1>{overview().find(item => item.eventType === AppEventType.Error)?.count ?? 0}</h1>
                </div>
            </div>
            <div class={styles.details}>
                <Show when={selectedType() !== null}>
                    <div class={styles.eventList}>
                        <For each={eventsForCurrentEventType()}>{(event) => (
                            <div class={styles.eventCard}>
                                <h3>{event.message}</h3>
                                <p>{event.where}</p>
                                <p>{formatDateTime(event.createdAt)}</p>
                            </div>
                        )}</For>
                    </div>
                </Show>
                <div class={styles.performanceSection}>
                    <input type="text" placeholder={t("APPEVENT_FILTER_LOCATION")} onInput={e => setLocationFilter(e.currentTarget.value || null)} value={locationFilter() ?? ""} />
                    <Show when={locationFilter()}>
                        <button onClick={() => setLocationFilter(null)}>{t("APPEVENT_CLEAR_FILTER")}</button>
                    </Show>
                    <h2>{t("APPEVENT_PERF_TITLE")}</h2>
                    <For each={filteredLocations()}>{(loc) => (
                        <div>
                            <h3>{loc}</h3>
                            <table class={styles.performanceTable}>
                                <thead>
                                    <tr>
                                        <th>{t("APPEVENT_MAX_TIME")}</th>
                                        <th>{t("APPEVENT_AVG_TIME")}</th>
                                        <th>{t("APPEVENT_MIN_TIME")}</th>
                                        <th>{t("APPEVENT_TOTAL_EVENTS")}</th>
                                        <th>{t("APPEVENT_ERROR_COUNT")}</th>
                                        <th>{t("APPEVENT_WARNING_COUNT")}</th>
                                        <th>{t("APPEVENT_INFO_COUNT")}</th>
                                        <th>{t("APPEVENT_DEBUG_COUNT")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(performanceDataForLocations()[loc] && performanceDataForLocations()[loc].totalEvents > 0) ? (
                                        <tr class={(performanceDataForLocations()[loc].avgTimeElapsed > 100 || performanceDataForLocations()[loc].maxTimeElapsed > 1000) ? styles.performanceWarning : ""}>
                                            <td>{performanceDataForLocations()[loc].maxTimeElapsed}</td>
                                            <td>{performanceDataForLocations()[loc].avgTimeElapsed}</td>
                                            <td>{performanceDataForLocations()[loc].minTimeElapsed}</td>
                                            <td>{performanceDataForLocations()[loc].totalEvents}</td>
                                            <td>{performanceDataForLocations()[loc].errorCount}</td>
                                            <td>{performanceDataForLocations()[loc].warningCount}</td>
                                            <td>{performanceDataForLocations()[loc].infoCount}</td>
                                            <td>{performanceDataForLocations()[loc].debugCount}</td>
                                        </tr>
                                    ) : (
                                        <tr>
                                            <td colSpan={8}>{t("APPEVENT_NO_DATA")}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}</For>
                </div>
            </div>
        </div>
    </div>
    );
}
